// PartnerDetail › Funding (docs/PARTNER_API.md § 4.5): the partner's fund per currency (what it may still load, what
// loads in progress hold, what it owes for advances), deposits / withdrawals / advances asked by one system
// administrator and approved by another, freezing its loads, its statement, and its funding settings.
import { shortDate } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { fundsApi, type Approval, type SettingValue } from '../api';
import { ApprovalsTable, approvalKind, approveQuestion } from '../components/Approvals';
import { useStaff } from '../components/RequireStaff';
import { SettingsForm } from '../components/SettingsForm';
import { confirmed } from '../components/ui';
import { PARTNER_FUNDING_SECTIONS } from './fundingSettings';

const REQUEST_KINDS = [
  ['deposit', 'Deposit (money received from the partner)'],
  ['withdrawal', 'Withdrawal (money paid back to the partner)'],
  ['advance', 'Advance (QOR lends; to be paid back)'],
  ['advance_repayment', 'Advance repaid'],
  ['adjustment', 'Adjustment (correction, + or −)'],
  ['write_off', 'Write off (QOR gives up on an advance for now; still owed)'],
  ['write_off_recovery', 'Written-off debt recovered'],
] as const;

export function PartnerFundingPanel({
  programId,
  after,
}: {
  programId: string;
  after: (ok: boolean, message: string) => void;
}) {
  const client = useQueryClient();
  const { user } = useStaff();
  const editable = user.role === 'SystemAdmin';
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const funds = useQuery({ queryKey: ['funds', programId], queryFn: () => fundsApi.programFunds(programId) });
  const settings = useQuery({ queryKey: ['funds-settings', programId], queryFn: () => fundsApi.settings(programId) });
  const entries = useQuery({
    queryKey: ['funds-entries', programId, page],
    queryFn: () => fundsApi.programEntries(programId, page),
  });
  const [request, setRequest] = useState({
    kind: 'deposit',
    amount: '',
    currency: 'USD',
    reference: '',
    memo: '',
    dueAt: '',
  });
  /** A deposit of the same amount told lately: staff must tick that this one is another wire. */
  const [sameAmount, setSameAmount] = useState<{ depositId: string; confirmed: boolean } | null>(null);

  async function refresh() {
    await Promise.all(
      ['funds', 'funds-settings', 'funds-entries', 'partner'].map((k) => client.invalidateQueries({ queryKey: [k] })),
    );
  }
  async function done(ok: boolean, message: string) {
    setBusy(false);
    after(ok, message);
    await refresh();
  }

  async function ask(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await fundsApi.requestFund(programId, {
      kind: request.kind,
      amount: request.amount.trim(),
      currency: request.currency,
      ...(request.reference.trim() && { reference: request.reference.trim() }),
      ...(request.memo.trim() && { memo: request.memo.trim() }),
      ...(request.kind === 'advance' &&
        request.dueAt && { dueAt: new Date(`${request.dueAt}T23:59:59Z`).toISOString() }),
      ...(request.kind === 'deposit' && sameAmount?.confirmed && { confirmNotDuplicate: true }),
    });
    if (r.ok) {
      setRequest({ ...request, amount: '', reference: '', memo: '', dueAt: '' });
      setSameAmount(null);
    } else if (r.code === 'DEPOSIT_POSSIBLE_DUPLICATE')
      setSameAmount({ depositId: String(r.details.depositId), confirmed: false });
    await done(
      r.ok,
      r.ok ? `${approvalKind(r.data.kind)} asked: a second system administrator must approve it.` : r.error,
    );
  }

  async function decide(a: Approval, approve: boolean) {
    if (approve && !confirmed(approveQuestion(a))) return;
    const note = approve ? null : window.prompt('Why (optional)?');
    if (!approve && note === null) return;
    setBusy(true);
    const r = approve ? await fundsApi.approve(a.id) : await fundsApi.reject(a.id, note?.trim() || null);
    await done(r.ok, r.ok ? (approve ? 'Approved and done.' : 'Turned down.') : r.error);
  }

  async function createOwnFund() {
    if (
      !confirmed(
        'Give this location its own fund? From now on its loads draw only on it (starting at 0.00) and never on its partner’s. This cannot be undone.',
      )
    )
      return;
    setBusy(true);
    const r = await fundsApi.createOwnFund(programId);
    await done(r.ok, r.ok ? 'This location now has its own fund: ask for a deposit below.' : r.error);
  }

  async function freeze() {
    if (!confirmed('Freeze every new load onto this partner’s cards? Lifting it needs a second system administrator.'))
      return;
    setBusy(true);
    const r = await fundsApi.freeze(programId);
    await done(r.ok, r.ok ? 'Loads frozen for this partner.' : r.error);
  }
  async function resume() {
    setBusy(true);
    const r = await fundsApi.resume(programId);
    await done(r.ok, r.ok ? 'Asked to resume loads: a second system administrator must approve it.' : r.error);
  }

  async function saveSettings(change: { set: Record<string, SettingValue>; clear: string[] }) {
    setBusy(true);
    const r = await fundsApi.changeSettings(programId, change);
    await done(
      r.ok,
      r.ok
        ? r.data.approvalId
          ? 'Saved; the changes marked "Needs approval" wait for a second system administrator.'
          : 'Funding settings saved.'
        : r.error,
    );
    return r.ok ? {} : r.problems;
  }

  const f = funds.data;
  /** Deposits told and waiting (by the partner or by staff): the money received may be one of them. */
  const partnerTold = (f?.pending ?? []).filter((a) => a.kind === 'fund_deposit');
  if (funds.isPending) return null;
  if (!f) return <p className="qc-empty">This partner has no processor to hold funds at.</p>;
  return (
    <>
      {f.isLocation && (
        <div className="qc-panel" style={{ marginBottom: 20 }}>
          <p className="qc-field-hint" style={{ marginTop: 0 }}>
            This location draws on its partner&apos;s fund: below is {f.name}&apos;s.
          </p>
          {editable && (
            <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void createOwnFund()}>
              Give this location its own fund
            </button>
          )}
        </div>
      )}
      {f.ownFund && (
        <p className="qc-field-hint">
          This location has its own fund: its loads draw only on it, and its deposits go to it.
        </p>
      )}
      <div className="qc-panel">
        <div className="qc-panel-title">
          <h3>Fund</h3>
          <span>
            {f.enabled
              ? 'Prefunded: every load onto this partner’s cards draws on this fund; a load without enough funds is refused.'
              : 'Not prefunded: loads do not use this fund (switch "Prefunded" on below).'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <span className={`qc-badge ${f.enabled ? 'qc-badge-success' : 'qc-badge-muted'}`}>
            {f.enabled ? 'Prefunded' : 'Not prefunded'}
          </span>
          {f.frozen && <span className="qc-badge qc-badge-danger">Loads frozen</span>}
          <span className="qc-badge qc-badge-muted">
            Advance limit: {f.advanceLimit === null ? 'none' : f.advanceLimit}
          </span>
        </div>
        {f.accounts.length === 0 ? (
          <p className="qc-empty">No money in this fund yet: ask for a deposit below.</p>
        ) : (
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Currency</th>
                <th>Available</th>
                <th>Held by loads in progress</th>
                <th>Owes for advances</th>
                <th>Written off</th>
                <th>Next due</th>
              </tr>
            </thead>
            <tbody>
              {f.accounts.map((a) => (
                <tr key={a.id}>
                  <td>{a.currency}</td>
                  <td>
                    <strong>{a.available}</strong>
                  </td>
                  <td>{a.held}</td>
                  <td>{a.advanceOutstanding}</td>
                  <td>{a.writtenOff}</td>
                  <td>
                    {a.nextAdvanceDueAt ? shortDate(a.nextAdvanceDueAt) : '—'}{' '}
                    {a.overdue && <span className="qc-badge qc-badge-danger">Overdue</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {editable && (
          <div style={{ marginTop: 14 }}>
            {f.frozen ? (
              <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void resume()}>
                Ask to resume loads
              </button>
            ) : (
              <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void freeze()}>
                Freeze loads
              </button>
            )}
          </div>
        )}
      </div>

      {f.locations.length > 0 && (
        <div className="qc-panel" style={{ marginTop: 20 }}>
          <div className="qc-panel-title">
            <h3>Locations with their own fund</h3>
            <span>Their loads draw on their own fund, not on this one. Open a location to manage its fund.</span>
          </div>
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Location</th>
                <th>Currency</th>
                <th>Available</th>
                <th>Held by loads in progress</th>
                <th>Owes for advances</th>
              </tr>
            </thead>
            <tbody>
              {f.locations.flatMap((l) =>
                (l.accounts.length ? l.accounts : [null]).map((a, i) => (
                  <tr key={`${l.programId}-${a?.id ?? 'none'}`}>
                    <td>{i === 0 && <Link to={`/partners/${l.programId}?tab=funding`}>{l.name}</Link>}</td>
                    <td>{a?.currency ?? '—'}</td>
                    <td>
                      <strong>{a?.available ?? '0.00'}</strong>
                    </td>
                    <td>{a?.held ?? '0.00'}</td>
                    <td>{a?.advanceOutstanding ?? '0.00'}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      )}

      {editable && (
        <div className="qc-panel" style={{ marginTop: 20 }}>
          <div className="qc-panel-title">
            <h3>Ask for a change</h3>
            <span>Done only when a second system administrator approves it (checked again then).</span>
          </div>
          {request.kind === 'deposit' && partnerTold.length > 0 && (
            <div className="qc-field-hint" style={{ marginBottom: 14 }} role="note">
              <strong>Already told, waiting for approval.</strong> If the money you received is one of these, approve it
              in &ldquo;Waiting for approval&rdquo; below instead of recording it again:
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {partnerTold.map((d) => (
                  <li key={d.id}>
                    {d.payload.amount} {d.payload.currency} · ref {d.payload.reference ?? '—'} · told by{' '}
                    {d.requestedBy.label} on {shortDate(d.createdAt)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <form onSubmit={(e) => void ask(e)} className="qc-account-field">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              <label>
                <span>What</span>
                <select
                  value={request.kind}
                  onChange={(e) => {
                    setRequest({ ...request, kind: e.target.value });
                    setSameAmount(null);
                  }}
                >
                  {REQUEST_KINDS.map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Amount</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    required
                    placeholder={request.kind === 'adjustment' ? '-25.00 or 25.00' : '0.00'}
                    value={request.amount}
                    onChange={(e) => {
                      setRequest({ ...request, amount: e.target.value });
                      setSameAmount(null);
                    }}
                  />
                  <select
                    value={request.currency}
                    aria-label="Currency"
                    onChange={(e) => setRequest({ ...request, currency: e.target.value })}
                    style={{ width: 90 }}
                  >
                    {['USD', 'CAD', 'EUR'].map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </label>
              <label>
                <span>
                  {request.kind === 'deposit' ? 'Bank reference of the wire (required)' : 'Reference (wire / ACH)'}
                </span>
                <input
                  type="text"
                  required={request.kind === 'deposit'}
                  value={request.reference}
                  onChange={(e) => setRequest({ ...request, reference: e.target.value })}
                />
              </label>
              {request.kind === 'advance' ? (
                <label>
                  <span>Due (empty: the partner&apos;s default)</span>
                  <input
                    type="date"
                    value={request.dueAt}
                    onChange={(e) => setRequest({ ...request, dueAt: e.target.value })}
                  />
                </label>
              ) : (
                <span />
              )}
            </div>
            <label>
              <span>Note{request.kind === 'adjustment' || request.kind === 'write_off' ? ' (required: why)' : ''}</span>
              <input
                type="text"
                required={request.kind === 'adjustment' || request.kind === 'write_off'}
                value={request.memo}
                onChange={(e) => setRequest({ ...request, memo: e.target.value })}
              />
            </label>
            {request.kind === 'deposit' && sameAmount && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  style={{ width: 18, height: 18 }}
                  checked={sameAmount.confirmed}
                  onChange={(e) => setSameAmount({ ...sameAmount, confirmed: e.target.checked })}
                />
                <span>
                  A deposit of the same amount was told lately
                  {partnerTold.some((d) => d.id === sameAmount.depositId) && ' (listed above)'}. I checked the bank
                  references: this is another wire.
                </span>
              </label>
            )}
            <div>
              <button
                type="submit"
                className="qor-button qor-button-primary"
                disabled={busy || (request.kind === 'deposit' && sameAmount !== null && !sameAmount.confirmed)}
              >
                Ask for approval
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Waiting for approval</h3>
        </div>
        <ApprovalsTable
          items={f.pending}
          busy={busy}
          onDecide={(a, ok) => void decide(a, ok)}
          onChanged={(ok, message) => void done(ok, message)}
        />
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Funding settings</h3>
          <span>This partner&apos;s own values; empty fields follow the platform&apos;s.</span>
        </div>
        {settings.data && (
          <SettingsForm
            key={funds.dataUpdatedAt + settings.dataUpdatedAt}
            rows={settings.data.filter((r) => r.group === 'funding')}
            sections={PARTNER_FUNDING_SECTIONS}
            editable={editable}
            busy={busy}
            onSave={saveSettings}
          />
        )}
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Statement</h3>
          <span>Every movement of the fund, newest first.</span>
        </div>
        {!entries.data || entries.data.items.length === 0 ? (
          <p className="qc-empty">No movements yet.</p>
        ) : (
          <>
            <table className="qc-plain-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th>Amount</th>
                  <th>Balance after</th>
                  <th>Reference / note</th>
                </tr>
              </thead>
              <tbody>
                {entries.data.items.map((e) => (
                  <tr key={e.id}>
                    <td>{shortDate(e.createdAt)}</td>
                    <td>
                      {e.kind.replace(/_/g, ' ')}
                      {e.advance !== '0.00' && (
                        <>
                          <br />
                          <small>owed {e.advance}</small>
                        </>
                      )}
                    </td>
                    <td>{e.amount}</td>
                    <td>{e.balanceAfter}</td>
                    <td>
                      {[e.reference, e.memo, e.dueAt && `due ${e.dueAt.slice(0, 10)}`].filter(Boolean).join(' · ') ||
                        (e.loadId ? 'Card load' : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="qc-pagination">
              <span>
                Page {entries.data.page} · {entries.data.total} total
              </span>
              <div className="qc-pagination-links">
                {page > 1 && (
                  <button type="button" className="qc-button-ghost" onClick={() => setPage(page - 1)}>
                    Previous
                  </button>
                )}
                {page * entries.data.pageSize < entries.data.total && (
                  <button type="button" className="qc-button-ghost" onClick={() => setPage(page + 1)}>
                    Next
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
